/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-empty-function */
import { PlatformLocation } from '@angular/common';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep, noop } from 'lodash-es';
import { BehaviorSubject, Subject, lastValueFrom, of } from 'rxjs';
import { catchError, debounceTime, map, takeUntil } from 'rxjs/operators';

import {
  Comment,
  Company,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  Field,
  FieldKinds,
  FormulasVersions,
  FormAttachment,
  HeaderFooterDefaultFields,
  HeaderFooterField,
  HeaderFooterFieldKinds,
  List,
  Template,
  TemplateOrientations,
} from '@site-mate/dashpivot-shared-library';

import { TemplatesService } from 'app/apps/templates.service';
import { FormManualSignatureComponent } from 'app/form/form-components/form-manual-signature.component';
import { FormService } from 'app/form/form.service';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { ListsService } from 'app/lists/lists.service';
import { SegmentService } from 'app/segment/segment.service';
import { FormWeb } from 'app/shared/model/form.model';
import { FieldWeb } from 'app/shared/model/item.model';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { IdNormalizer } from 'app/shared/model/utils/id-normalizer.model';
import { ConfirmService, confirmExitOperations } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { FormValidationService } from 'app/shared/service/form-validation.service';
import { FormulaEngineService } from 'app/shared/service/formula-engine.service';
import { HotFormulaParserEngineService } from 'app/shared/service/hot-formula-parser-engine.service';
import { HyperFormulaEngineService } from 'app/shared/service/hyper-formula-engine.service';
import { LogicRuleService } from 'app/shared/service/logic-rules.service';
import { MetaDataService } from 'app/shared/service/meta-data.service';
import { ProjectService } from 'app/shared/service/project.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { ProfileService } from 'app/user/profile.service';
import { UserService } from 'app/user/user.service';

import { ResetWorkflowComponent } from './reset-workflow/reset-workflow.component';
import { FormModalAction } from '../../apps/template-form-edit/form-modal-action.model';
import { FormEventsService } from '../form-events.service';
import { FormItemsChangeDetector } from '../form-item-change-detectors/form-items-change-detector';
import { FormUtil } from '../form-util.service';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class AddEditFormBaseComponent implements OnDestroy, OnInit {
  private readonly uploadBatchSize = 2;
  private readonly fieldDebounceTime = 500;
  readonly metadataList$ = new BehaviorSubject<List[]>(null);

  colorPickerState$ = new Subject<boolean>();

  originalItems: FieldWeb[];

  @ViewChild('submitButton') submitButton: ElementRef;
  @Input() scrollTop = 0;

  unsub = new Subject<void>();
  isWorking: boolean;
  isCloning: boolean;
  canSave: boolean;
  editForm: boolean;
  showActions: boolean;
  showActivities: boolean;
  saving: boolean;
  form: FormWeb;
  template: Template;
  attachmentUploading: { id: number; name: string }[] = [];
  isLibraryView: boolean;
  isPreview: boolean;
  formOrientation: TemplateOrientations;
  isTemplateEditorPreview = false;
  navigateOnSignUpToFormModal: boolean;

  readonly templateOrientations = TemplateOrientations;
  readonly headerFooterKind = HeaderFooterFieldKinds;

  teamName: string;
  parentId: string;
  projectName: string;
  company: Company;
  errorToastLife = 5 * 60 * 1000;
  renderedFields: Field[];
  activities: Comment[] = [];
  filePath = '';
  isHeaderSettingsLoaded = false;
  headerFields: HeaderFooterField[] = [];
  formulaEngineService: FormulaEngineService;

  constructor(
    protected readonly location: PlatformLocation,
    protected readonly activeModal: NgbActiveModal,
    protected readonly i18nService: TmpI18NService,
    protected readonly listsService: ListsService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly toastr: ToastrService,
    protected readonly confirmService: ConfirmService,
    protected readonly formService: FormService,
    protected readonly modal: NgbModal,
    protected readonly metaDataService: MetaDataService,
    protected readonly profileService: ProfileService,
    protected readonly projectService: ProjectService,
    protected readonly segmentService: SegmentService,
    protected readonly userService: UserService,
    protected readonly formValidationService: FormValidationService,
    protected readonly logicRuleService: LogicRuleService,
    protected readonly hostElement: ElementRef,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly templatesService: TemplatesService,
    protected readonly injector: Injector,
    protected readonly formEventsService: FormEventsService,
  ) {}

  ngOnInit() {
    this.formulaEngineService = this.getFormulasServiceEngine();
    this.formService.onFieldChanged
      .pipe(takeUntil(this.unsub), debounceTime(this.fieldDebounceTime))
      .subscribe(() => {
        this.canSave = true;
      });
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }

  get isFormReady() {
    return this.isHeaderSettingsLoaded && this.form;
  }

  abstract onClose();

  abstract beforeDismiss();

  remove() {
    throw new Error('Not implemented');
  }

  download() {
    throw new Error('Not implemented');
  }

  onSend() {
    throw new Error('Not implemented');
  }

  clone() {
    throw new Error('Not implemented');
  }

  onUseThisTemplate() {
    throw new Error('Not implemented');
  }

  trackFormFields(_index, item) {
    return `${item.id}-${Boolean(item.readOnly)}`;
  }

  decorateAndCopyItems() {
    FormUtil.decorateItems(this.form.items);
    this.originalItems = cloneDeep(FormUtil.undecorateItems(this.form.items));
  }

  setupContext(template) {
    const { team, project, company } = template;
    this.teamName = team.name;
    this.parentId = new IdNormalizer(project).value();
    this.projectName = project.name;
    this.company = company;
    this.filePath = `${this.company.name}/${this.projectName}/${this.teamName}`;
  }

  setupForm(form) {
    // Setting the template before the form to avoid potential racing condition with multiple form.items association
    this.template = form.template;
    this.form = form;
    this.form.items = this.logicRuleService.evaluateLogicRules(this.form.items);
    this.decorateAndCopyItems();
    this.form.items = this.setupLogicRuleFields(this.form.items);
    this.formOrientation = form.template.orientation;
    this.setupContext(form.template);
    this.setAutomatedFormNumber();
    this.setupMetadataList(form.template);
    this.initFormulaEngine();
  }

  resetFormFields(fieldsToReset: Field[]) {
    const resetFields = this.logicRuleService.resetFields({
      fieldsToReset,
      templateFields: this.template.items,
      formItems: this.form.items,
      formulaEngineService: this.formulaEngineService,
    });

    this.form.items = this.setupLogicRuleFields(resetFields);
    this.formService.onFieldChanged.next();
  }

  private setupLogicRuleFields(formFields: FieldWeb[]): FieldWeb[] {
    return formFields.map((field) => {
      if (this.logicRuleService.hasLogicRules(field)) {
        field.logicRules.forEach((rule) => {
          rule.fields = formFields
            .filter((formField) => this.logicRuleService.dependsOnAnotherField(formField))
            .filter((formField) => this.logicRuleService.dependsOnRule(formField, rule));
        });
      }

      return field;
    });
  }

  setupMetadataList(template) {
    const { project, company } = template;
    if (template.readOnly) {
      this.listsService.getCompanyMetaDataList(company._id).subscribe(
        (lists) => {
          const filteredLists = lists.map((list) => {
            const localList = ListsHandler.service.filterDeployedListItems({ ...list }, this.parentId);
            localList.rows = localList.rows?.sort((previousRow, nextRow) =>
              previousRow.localeCompare(nextRow),
            );
            return localList;
          });
          this.metadataList$.next(filteredLists);
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
    } else {
      this.listsService.getProjectMetaDataList(project._id).subscribe(
        (lists) => {
          const filteredLists = lists.map((list) => {
            const localList = ListsHandler.service.filterDeployedListItems({ ...list }, this.parentId);
            localList.items = localList.items?.sort((previousList, nextList) => {
              return previousList.value && String.prototype.localeCompare
                ? String(previousList.value).localeCompare(String(nextList))
                : 0;
            });
            return localList;
          });
          this.metadataList$.next(filteredLists);
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
    }
  }

  initializeHeaderSettings(templateId: string) {
    this.templatesService
      .byId(templateId)
      .pipe(takeUntil(this.unsub))
      .subscribe(
        (template: Template) => {
          this.headerFields = template.headerItems
            ? template.headerItems.filter((field) => field.isVisible !== false)
            : HeaderFooterDefaultFields.header;
          this.isHeaderSettingsLoaded = true;
        },
        (error) => {
          this.errorHandler.handle(error);
          this.isHeaderSettingsLoaded = true;
        },
      );
  }

  isValidForSigning(itemId: string) {
    return (
      this.formValidationService.isValidForSigning(this.form, itemId) &&
      this.formValidationService.isValid(this.form)
    );
  }

  isValid(ignoreRequiredValidation?: boolean) {
    return this.formValidationService.isValid(this.form, ignoreRequiredValidation);
  }

  onToggleActivities() {
    this.showActivities = !this.showActivities;
  }

  async onSign(itemId, formId, items) {
    if (!this.isValidForSigning(itemId)) {
      this.toastr.error(this.i18nService.getMessage('invalidForm'));
      return;
    }

    const itemsCleaned = FormUtil.undecorateItems(items);

    try {
      if (FormItemsChangeDetector.hasAnyItemChanged(this.originalItems, itemsCleaned)) {
        await this.confirmService.confirm('saveBeforeSign', {
          confirmButtonText: this.i18nService.getMessage('saveAndSign'),
        });
        await this.formService.updateItems(formId, itemsCleaned).toPromise();
      } else {
        await this.confirmService.confirmSign('signForm');
      }

      await this.formService.sign(this.form.id, itemId).toPromise();
      this.formulaEngineService.destroy();
      this.activeModal.close({
        reason: FormModalAction.Sign,
        formId: this.form.id,
        scrollTop: this.hostElement.nativeElement.scrollTop,
      });
      this.toastr.successByKey('formSaved');
    } catch (error) {
      if (!confirmExitOperations.includes(error)) {
        this.errorHandler.handle(error);
      }
    }
  }

  async onResetSignature() {
    const itemsCleaned = FormUtil.undecorateItems(this.form.items);
    const formChanged = FormItemsChangeDetector.hasAnyItemChanged(this.originalItems, itemsCleaned);
    try {
      const modalRef = this.modal.open(ResetWorkflowComponent, {
        windowClass: 'modal-500 modal-overlay',
        modalDialogClass: 'modal-dialog-centered',
      });
      modalRef.componentInstance.form = this.form;
      modalRef.componentInstance.submitButtonLabel = formChanged ? 'Save and Reset' : 'Save';

      const selectedColumnId = await modalRef.result;

      if (formChanged) {
        await lastValueFrom(this.formService.updateItems(this.form.id, itemsCleaned));
      }

      await this.resetSignature(selectedColumnId);
    } catch (error) {
      if (error && !confirmExitOperations.includes(error)) {
        this.errorHandler.handle(error);
      }
    }
  }

  async resetSignature(selectedColumnId: string) {
    try {
      await this.formService.resetSignature(this.form.id, selectedColumnId).toPromise();
      this.formulaEngineService.destroy();
      this.activeModal.close({
        reason: FormModalAction.ResetSignature,
        formId: this.form.id,
        scrollTop: this.hostElement.nativeElement.scrollTop,
      });
      this.formService.onFieldChanged.next();
    } catch (error) {
      this.errorHandler.handle(error);
    }
  }

  onDeleteAttachment({
    attachment,
    attachments,
  }: {
    attachment: FormAttachment;
    attachments: FormAttachment[];
  }) {
    this.confirmService
      .confirmDelete('deleteAttachment')
      .then(() => {
        this.form.dynamicAttachments = attachments.filter((attachToRemove) => {
          const removableAttachmentId = new IdNormalizer(attachToRemove).value();
          const currentAttachmentId = new IdNormalizer(attachment).value();
          return removableAttachmentId !== currentAttachmentId;
        });
        return this.updateFormAttachments();
      })
      .catch(() => {});
  }

  async handleUploadAttachment(event) {
    await this.formService.handleAttachmentUpload({
      uploadBatchSize: this.uploadBatchSize,
      target: event.target,
      uploader: (fakeId, formData) => {
        return this.formService.uploadFile(this.form.id, formData).pipe(
          map((resultAttachment) => {
            this.form.dynamicAttachments = this.form.dynamicAttachments || [];
            this.form.dynamicAttachments.push(resultAttachment);
            this.attachmentUploading = this.attachmentUploading.filter((u) => u.id !== fakeId);
            void EventNotifierService.notify(
              new DashpivotEvent(EventTypes.FormAttachmentAdded),
              this.segmentService,
            );
            return resultAttachment;
          }),
          catchError((error) => {
            this.errorHandler.handle(error, null, null, {
              enableHTML: true,
              toastLife: this.errorToastLife,
            });
            this.attachmentUploading = this.attachmentUploading.filter((u) => u.id !== fakeId);
            return of(null);
          }),
        );
      },
    });

    this.updateFormAttachments();
  }

  private updateFormAttachments() {
    this.formService
      .updateItems(this.form.id, null, { dynamicAttachments: this.form.dynamicAttachments })
      .subscribe(
        () => {},
        (error) => this.errorHandler.handle(error),
      );
  }

  pushSignature(model, signature) {
    const userId = this.userService.getCurrentUser().id;
    if (!model.signatures) {
      model.signatures = [];
    }

    const newSignature = {
      fullName: signature.fullName,
      signatureUrl: signature.signatureUrl,
      companyName: signature.companyName,
      signedOn: signature.signedOn,
      userId,
    };

    model.signatures.push(newSignature);
    this.changeDetectorRef.detectChanges();
  }

  handleNormalSign(model) {
    if (!this.profileService.hasProfileSignature()) {
      return;
    }
    if (this.form.id) {
      this.confirmService
        .confirmSign('signForm')
        .then(() => {
          return this.formService
            .sign(this.form.id, model.id)
            .pipe(takeUntil(this.unsub))
            .subscribe((res) => {
              this.pushSignature(model, res);
              this.formService.onFieldChanged.next();
              model._invalid = false;
            }, this.errorHandler.handle.bind(this.errorHandler));
        })
        .catch(noop);
    }
  }

  async handleApprovalSign({ id }) {
    if (!this.profileService.hasProfileSignature()) {
      return;
    }

    if (this.form.id) {
      await this.onSign(id, this.form.id, this.form.items);
    }
  }

  handleManualSign(model) {
    if (this.form.id) {
      const modalRef = this.modal.open(FormManualSignatureComponent);
      modalRef.result
        .then((signature) => {
          return this.formService.manualSign(this.form.id, model.id, signature).subscribe(
            (res) => {
              this.pushSignature(model, res);
              model._invalid = false;
              this.formService.onFieldChanged.next();
            },
            (error) => this.errorHandler.handle(error),
          );
        })
        .catch(() => {});
    }
  }

  private setAutomatedFormNumber() {
    if (this.form.automatedFormNumber) {
      return;
    }

    const defaultPlaceholder = '[Folder path]';
    const prefix = this.template.isOrganisationTemplate
      ? [defaultPlaceholder]
      : [this.company.name, this.projectName, this.teamName];

    const counterComponents = [...prefix, this.template.uniqueAppId, this.form.counter];

    this.form.automatedFormNumber = counterComponents.every((comp) => !!comp)
      ? counterComponents.join('-')
      : '';
  }

  initFormulaEngine() {
    this.formulaEngineService = this.getFormulasServiceEngine();
    this.formulaEngineService.init();
    this.initSheets();
    this.formulaEngineService.setValuesUpdateListener((changes) => {
      this.formEventsService.emitFormulaFieldChangedEvent(changes);
    });
  }

  private initSheets() {
    this.form.items.filter(this.isTableField).forEach(({ tableReference }) => {
      this.formulaEngineService.addSheet(tableReference);
      this.formulaEngineService.setEmptySheetContent(tableReference);
    });
  }

  private isTableField(field: Field): boolean {
    return [FieldKinds.Table, FieldKinds.PrefilledTable].includes(field.kind);
  }

  private get isTemplateFormulasVersion2(): boolean {
    return this.template?.formulasVersion === FormulasVersions.V2;
  }

  private getFormulasServiceEngine(): FormulaEngineService {
    return this.isTemplateFormulasVersion2
      ? this.injector.get<any>(HyperFormulaEngineService)
      : this.injector.get<FormulaEngineService>(HotFormulaParserEngineService);
  }
}
